<template>
  <div>
    <v-progress-linear v-if="loadingOffers" indeterminate color="primary" />

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <v-toolbar flat dense class="mb-1">
        <v-spacer />
        <excel-button class="mr-2" @click="exportExcel" />
        <print-button class="mr-3" @click="print" />
        <circle-button
          icon="mdi-file-plus-outline"
          :tooltip="$t('warehouse.send_offer')"
          color="teal"
          @click="openOfferDialog"
        />
      </v-toolbar>

      <mobile-data-cards
        v-if="items.length > 0 || loadingOffers"
        :headers="mobileHeaders"
        :items="items"
        :loading="loadingOffers"
        item-key="id"
        :show-row-number="false"
      >
        <template v-slot:item.document_type="{ item }">
          <v-chip
            small
            :color="item.document_type === 'invoice' ? 'blue darken-1' : 'teal'"
            dark
          >
            {{
              $t(
                item.document_type === "invoice" ? "warehouse.invoice" : "warehouse.offer"
              )
            }}
          </v-chip>
        </template>
        <template v-slot:item.document_date="{ item }">
          {{ localeDate(item.document_date) }}
        </template>
        <template v-slot:item.restaurant_id="{ item }">
          {{ toLatin(restaurantMap[item.restaurant_id] || "-") }}
        </template>
        <template v-slot:item.actions="{ item }">
          <circle-button
            icon="mdi-file-document-edit-outline"
            :tooltip="$t('overview')"
            color="primary"
            @click="openOfferDialog(item)"
          />
        </template>
      </mobile-data-cards>
      <empty-state
        v-if="!loadingOffers && items.length === 0"
        icon="mdi-file-document-outline"
        :title="$t('no_data')"
      />
    </template>

    <!-- Desktop View -->
    <template v-else>
      <empty-state
        v-if="!loadingOffers && items.length === 0"
        icon="mdi-file-document-outline"
        :title="$t('no_data')"
      />
      <v-data-table
        v-else
        :headers="headers"
        :items="items"
        :loading="loadingOffers"
        class="striped-table ordera-box-shadow"
        :items-per-page="15"
      >
        <template v-slot:top>
          <v-toolbar flat dense>
            <v-spacer />
            <excel-button class="mr-2" @click="exportExcel" />
            <print-button class="mr-3" @click="print" />
            <circle-button
              icon="mdi-file-plus-outline"
              :tooltip="$t('warehouse.send_offer')"
              color="teal"
              class="ml-2"
              @click="openOfferDialog"
            />
          </v-toolbar>
        </template>

        <template v-slot:item.document_type="{ item }">
          <v-chip
            small
            :color="item.document_type === 'invoice' ? 'blue darken-1' : 'teal'"
            dark
          >
            {{
              $t(
                item.document_type === "invoice" ? "warehouse.invoice" : "warehouse.offer"
              )
            }}
          </v-chip>
        </template>
        <template v-slot:item.document_date="{ item }">
          {{ localeDate(item.document_date) }}
        </template>
        <template v-slot:item.restaurant_id="{ item }">
          {{ toLatin(restaurantMap[item.restaurant_id] || "-") }}
        </template>
        <template v-slot:item.notes="{ item }">
          <span v-if="item.notes" class="text-truncate d-block" style="max-width: 200px">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">{{ toLatin(item.notes) }}</span>
              </template>
              <span>{{ toLatin(item.notes) }}</span>
            </v-tooltip>
          </span>
          <span v-else class="grey--text">-</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <circle-button
            icon="mdi-file-document-edit-outline"
            :tooltip="$t('overview')"
            color="primary"
            @click="openOfferDialog(item)"
          />
        </template>
      </v-data-table>
    </template>

    <supplier-portal-offers-invoices-dialog
      v-model="offerDialog"
      :edited-item="offerItem"
    />
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import warehouseFiltersMixin from "@/mixins/warehouse/warehouseFiltersMixin";
import hasPrintingV2 from "@/mixins/hasPrintingV2";
import hasExcel from "@/mixins/hasExcel";
import SupplierPortalOffersInvoicesDialog from "@/components/suppliers/SupplierPortalOffersInvoicesDialog.vue";
import { orderDocumentGrocery } from "@/models/warehouse";
import warehouseService from "@/services/warehouse";

export default {
  name: "SupplierPortalOffersInvoicesTab",
  mixins: [warehouseFiltersMixin, hasPrintingV2, hasExcel],
  components: { EmptyState, MobileDataCards, SupplierPortalOffersInvoicesDialog },
  props: {
    dateRange: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    docType: { type: String, default: null },
  },
  data() {
    return {
      offers: [],
      loadingOffers: false,
      offerDialog: false,
      offerItem: { ...orderDocumentGrocery, groceries: [] },
    };
  },
  computed: {
    items() {
      if (!this.docType) return this.offers;
      return this.offers.filter((o) => o.document_type === this.docType);
    },
    restaurantMap() {
      const restaurants = this.$store.state.user.user?.restaurants || [];
      return Object.fromEntries(restaurants.map((r) => [r.id, r.name]));
    },
    headers() {
      return [
        { text: "ID", value: "id" },
        { text: this.$t("type"), value: "document_type" },
        { text: this.$t("date"), value: "document_date" },
        { text: this.$t("restaurant"), value: "restaurant_id" },
        { text: this.$t("note"), value: "notes" },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          align: "right",
          print: false,
        },
      ];
    },
    mobileHeaders() {
      return [
        { text: "ID", value: "id" },
        { text: this.$t("type"), value: "document_type" },
        { text: this.$t("date"), value: "document_date" },
        { text: this.$t("restaurant"), value: "restaurant_id" },
      ];
    },
    subtitle() {
      const [from, to] = [...(this.dateRange || [])].sort();
      if (!from) return "";
      const fromStr = this.localeDate(from);
      const toStr = to && to !== from ? this.localeDate(to) : null;
      return toStr ? `${fromStr} - ${toStr}` : fromStr;
    },
  },
  methods: {
    openOfferDialog(item = null) {
      if (item) {
        this.offerItem = { ...item, groceries: item.groceries || [] };
      } else {
        this.offerItem = { ...orderDocumentGrocery, groceries: [] };
      }
      this.offerDialog = true;
    },
    async fetchOffers() {
      const user = this.$store.state.user.user;
      const supplier = user.suppliers?.[0] || null;
      if (!supplier) return;

      const [from, to] = [...(this.dateRange || [])].sort();
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;

      try {
        this.loadingOffers = true;
        const { data } = await warehouseService.getOffers(params);
        this.offers = data;
      } catch (error) {
        console.error("Error fetching offers:", error);
      } finally {
        this.loadingOffers = false;
      }
    },
    getOfferStatusColor(status) {
      const map = {
        pending: "orange",
        accepted: "green",
        rejected: "red",
        paid: "green darken-2",
        unpaid: "red darken-1",
      };
      return map[status] || "grey";
    },
    _exportHeaders() {
      return this.headers;
    },
    _exportItems() {
      return this.items.map((item) => ({
        id: item.id,
        document_type: this.$t(
          item.document_type === "invoice" ? "warehouse.invoice" : "warehouse.offer"
        ),
        document_date: this.localeDate(item.document_date),
        restaurant_id: this.toLatin(this.restaurantMap[item.restaurant_id] || "-"),
        notes: this.toLatin(item.notes || ""),
      }));
    },
    print() {
      const { table, heading, spacer } = this.printElements;
      const title = this.$t("warehouse.offers_invoices");
      const user = this.$store.state.user.user;
      const supplier = (user.suppliers && user.suppliers[0]) || null;
      const supplierName = supplier ? this.toLatin(supplier.name) : "";
      this.printDoc(
        [
          heading(title),
          this.subtitle
            ? `<div style="text-align:center;font-size:14px;color:#555;margin-top:4px">${this.subtitle}</div>`
            : "",
          supplierName
            ? `<div style="text-align:center;font-size:13px;font-weight:600;margin-top:4px">${supplierName}</div>`
            : "",
          spacer(8),
          table(this._exportHeaders(), this._exportItems()),
        ],
        { pageTitle: title }
      );
    },
    async exportExcel() {
      const title = this.$t("warehouse.offers_invoices");
      await this.downloadExcel({
        fileName: `${title}.xlsx`,
        sheets: [
          {
            name: title.substring(0, 31),
            title,
            subtitle: this.subtitle,
            headers: this._exportHeaders(),
            items: this._exportItems(),
          },
        ],
      });
    },
  },
  mounted() {
    this.fetchOffers();
  },
  watch: {
    dateRange() {
      this.fetchOffers();
    },
  },
};
</script>
